/**
 * NotificationSettingsPage — Claims Admin configures per-event notification toggles.
 * Blueprint: F-12 — new UI component, Claims Admin access only.
 *
 * MVP: Email toggles only. SMS and in-app channels show "Coming in Phase 2".
 * 12 event types from firm_notification_settings table.
 *
 * TODO: GET /firms/:id/notifications — load current settings.
 * TODO: PUT /firms/:id/notifications — save toggle state.
 * TODO: LOW_CREDIT_BALANCE event uses existing billing threshold from credit system.
 */
import React, { useState } from 'react';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { Save } from 'lucide-react';
import type { NotificationSetting, NotificationEventType } from '@/types/cmm/claimant.types';

const BREADCRUMBS = [{ label: 'Admin' }, { label: 'Notification Settings' }];

const EVENT_LABELS: Record<NotificationEventType, string> = {
  STAGE_CHANGE:          'Stage Change',
  DEFICIENCY_RAISED:     'Deficiency Raised',
  DEFICIENCY_CLEARED:    'Deficiency Cleared',
  DOCUMENT_UPLOADED:     'Document Uploaded',
  DOCUMENT_ACCEPTED:     'Document Accepted',
  DOCUMENT_REJECTED:     'Document Rejected',
  OVERLAP_DETECTED:      'Overlap Detected',
  OVERLAP_ACCEPTED:      'Overlap Accepted',
  OVERLAP_REJECTED:      'Overlap Rejected',
  PACKET_GENERATED:      'Packet Generated',
  PACKET_STATUS_ADVANCED:'Packet Status Advanced',
  LOW_CREDIT_BALANCE:    'Low Credit Balance',
};

const ALL_EVENTS = Object.keys(EVENT_LABELS) as NotificationEventType[];

const NotificationSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Record<NotificationEventType, boolean>>(
    Object.fromEntries(ALL_EVENTS.map(e => [e, true])) as Record<NotificationEventType, boolean>
  );
  const [saving, setSaving] = useState(false);

  const toggle = (event: NotificationEventType) =>
    setSettings(prev => ({ ...prev, [event]: !prev[event] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // TODO: PUT /firms/:firm_id/notifications
      console.warn('[NotificationSettingsPage] save — TODO: PUT /firms/:id/notifications', settings);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseLayout title="Notification Settings" subtitle="Configure email alerts for your firm"
      breadcrumbs={BREADCRUMBS}
      headerActions={
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 text-sm font-medium text-white bg-blue-600 rounded-md px-4 py-1.5 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      }>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden max-w-2xl">
        {/* Column headers */}
        <div className="grid grid-cols-4 px-5 py-3 border-b border-gray-200 bg-gray-50">
          <div className="col-span-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">Event</div>
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider text-center">Email</div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">SMS / In-App</div>
        </div>

        {ALL_EVENTS.map(event => (
          <div key={event} className="grid grid-cols-4 items-center px-5 py-3 border-b border-gray-100 hover:bg-gray-50">
            <div className="col-span-2">
              <p className="text-sm text-gray-800">{EVENT_LABELS[event]}</p>
              <p className="text-xs font-mono text-gray-400">{event}</p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => toggle(event)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  settings[event] ? 'bg-blue-600' : 'bg-gray-200'
                }`}>
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  settings[event] ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex justify-center">
              <span className="text-xs text-gray-300">Phase 2</span>
            </div>
          </div>
        ))}
      </div>
    </BaseLayout>
  );
};

export default NotificationSettingsPage;
